import React, { useEffect, useMemo, useState } from "react";
import ReactECharts from "echarts-for-react";
import * as echarts from "echarts";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FiArrowLeft,
  FiCalendar,
  FiLoader,
  FiRefreshCcw,
  FiDownload,
  FiTrendingUp,
  FiFlag,
  FiTarget,
} from "react-icons/fi";
import axiosInstance from "../../../Utils/axiosInstance";

export const MONTHS = [
  { label: "January", value: 1 },
  { label: "February", value: 2 },
  { label: "March", value: 3 },
  { label: "April", value: 4 },
  { label: "May", value: 5 },
  { label: "June", value: 6 },
  { label: "July", value: 7 },
  { label: "August", value: 8 },
  { label: "September", value: 9 },
  { label: "October", value: 10 },
  { label: "November", value: 11 },
  { label: "December", value: 12 },
];

export const YEAR = [{ label: "2026", value: 2026 }, { label: "2025", value: 2025 }];

const DEFAULT_MONTH = 3;
const DEFAULT_YEAR = 2026;
const chartColorPalettes = {
  factory: ["#4f6af2", "#35a57f", "#7c4dff", "#d97706", "#dc2626", "#3ba3ec"],
  onshore: ["#4f6af2", "#35a57f", "#6d4cf5", "#d97706", "#dc2626", "#3ba3ec"],
  service: ["#d97706", "#6d4cf5", "#3ba3ec", "#4f6af2", "#dc2626", "#35a57f"],
};

const segmentStyles = {
  Factory: {
    accent: "bg-gradient-to-r from-[#00C6A2] to-[#0060D0]", // Gradient
    icon: "🏭",
  },
  Onshore: {
    accent: "bg-[#00C6A2]", // Forest
    icon: "🏗️",
  },
  Service: {
    accent: "bg-[#0060D0]", // Teal
    icon: "🛠️",
  },
};

const defaultSummaryCards = [
  {
    title: "Factory",
    accent: segmentStyles.Factory.accent,
    icon: segmentStyles.Factory.icon,
    baselinePerYear: 45.83,
    metrics: [
      { label: "Actual Per Month", value: 2.37 },
      { label: "Baseline Per Month", value: 3.83 },
      { label: "Target Per Month", value: 3.83 },
    ],
    xlsLink: null,
  },
  {
    title: "Onshore",
    accent: segmentStyles.Onshore.accent,
    icon: segmentStyles.Onshore.icon,
    baselinePerYear: 75.46,
    metrics: [
      { label: "Actual Per Month", value: 316.33 },
      { label: "Baseline Per Month", value: 6.28 },
      { label: "Target Per Month", value: 3.16 },
    ],
    xlsLink: null,
  },
  {
    title: "Service",
    accent: segmentStyles.Service.accent,
    icon: segmentStyles.Service.icon,
    baselinePerYear: 205.2,
    metrics: [
      { label: "Actual Per Month", value: 20.1 },
      { label: "Baseline Per Month", value: 17.1 },
      { label: "Target Per Month", value: 11.26 },
    ],
    xlsLink: null,
  },
];

const defaultDonutSections = [
  {
    title: "Factory - cost split by BP category",
    subtitle: "Actuals Rs Cr",
    total: 2.37,
    centerLabel: "Rs Cr total",
    items: [
      { name: "Facility Management Costs", value: 0.96, color: "#4f6af2" },
      { name: "Insurance", value: 0.15, color: "#35a57f" },
      {
        name: "Warehouse operations expenses & Consumables",
        value: 0.43,
        color: "#7c4dff",
      },
      { name: "Repair & Maintenance", value: 0.04, color: "#d97706" },
      { name: "Others", value: 0.79, color: "#dc2626" },
    ],
  },
  {
    title: "Onshore - cost split by BP category",
    subtitle: "Actuals Rs Cr",
    total: 316.33,
    centerLabel: "Rs Cr total",
    items: [
      { name: "Facility Management Costs", value: 316.56, color: "#4f6af2" },
      { name: "Insurance", value: 0.42, color: "#35a57f" },
      {
        name: "Warehouse operations expenses & Consumables",
        value: -2.77,
        color: "#6d4cf5",
      },
      { name: "Repair & Maintenance", value: 0.46, color: "#d97706" },
      { name: "Others", value: 1.66, color: "#dc2626" },
    ],
  },
  {
    title: "Service - cost split by BP category",
    subtitle: "Actuals Rs Cr",
    total: 20.1,
    centerLabel: "Rs Cr total",
    items: [
      { name: "Manpower & Security", value: 15.13, color: "#d97706" },
      { name: "Insurance & Medical Checkup", value: 1.77, color: "#6d4cf5" },
      { name: "Travel & Logistics", value: 2.46, color: "#3ba3ec" },
      { name: "BOP", value: 0.04, color: "#4f6af2" },
      {
        name: "SCADA & IT Communication, Training, Safety, Tools & Site Expenses",
        value: 0.7,
        color: "#dc2626",
      },
      { name: "Rent & Compliance", value: 0, color: "#35a57f" },
    ],
  },
];

const defaultTrendSeries = {
  labels: ["Jan-2026", "Feb-2026", "Mar-2026"],
  factory: [0, -2.41, 2.37],
  onshore: [0, 105.43, 316.33],
  service: [0, 48.45, 20.1],
};

const formatDashboardTitle = (name) =>
  name
    ?.toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase()) || "Dashboard Detail";

const formatValue = (value) => Number(value || 0).toFixed(2);

const getMonthShortLabel = (month, year) => {
  const monthLabel =
    MONTHS.find((item) => item.value === month)?.label?.slice(0, 3) || "Mar";
  return `${monthLabel}-${String(year).slice(-2)}`;
};

const mapChartItems = (items = [], palette = []) =>
  items.map((item, index) => ({
    name: item.Category,
    value: Number(item.ActualValue || 0),
    color: palette[index % palette.length],
  }));

const buildSummaryCard = (
  cardData,
  fallbackTitle,
  accent,
  icon,
  excelFiles = []
) => {
  const title =
    cardData?.card1 || cardData?.card2 || cardData?.card3 || fallbackTitle;
  const lowerCaseFallbackTitle = fallbackTitle.toLowerCase();
  const excelFile = excelFiles.find((file) =>
    file.id.toLowerCase().includes(lowerCaseFallbackTitle)
  );

  return {
    title,
    accent,
    icon,
    xlsLink: excelFile?.xlsLink || null,
    baselinePerYear: Number(cardData?.BaselinePerYear || 0),
    metrics: [
      {
        label: "Actual Per Month",
        value: Number(cardData?.ActualPerMonth || 0),
      },
      {
        label: "Baseline Per Month",
        value: Number(cardData?.BaselinePerMonth || 0),
      },
      {
        label: "Target Per Month",
        value: Number(cardData?.TargetPerMonth || 0),
      },
    ],
  };
};

const buildDashboardData = (items, selectedMonth, selectedYear) => {
  const factoryCard = items?.card1?.[0] || {};
  const onshoreCard = items?.card2?.[0] || {};
  const serviceCard = items?.card3?.[0] || {};
  const monthShortLabel = getMonthShortLabel(selectedMonth, selectedYear);
  const excelFiles = items?.excelFiles || [];

  return {
    summaryCards: [
      buildSummaryCard(
        factoryCard,
        "Factory",
        segmentStyles.Factory.accent,
        segmentStyles.Factory.icon,
        excelFiles
      ),
      buildSummaryCard(
        onshoreCard,
        "Onshore",
        segmentStyles.Onshore.accent,
        segmentStyles.Onshore.icon,
        excelFiles
      ),
      buildSummaryCard(
        serviceCard,
        "Service",
        segmentStyles.Service.accent,
        segmentStyles.Service.icon,
        excelFiles
      ),
    ],
    donutSections: [
      {
        title: "Factory - cost split by BP category",
        subtitle: "Actuals Rs Cr",
        total: Number(items?.chart1?.[0]?.ActualTotalValue || 0),
        centerLabel: "Rs Cr total",
        items: mapChartItems(items?.chart1, chartColorPalettes.factory),
      },
      {
        title: "Onshore - cost split by BP category",
        subtitle: "Actuals Rs Cr",
        total: Number(items?.chart2?.[0]?.ActualTotalValue || 0),
        centerLabel: "Rs Cr total",
        items: mapChartItems(items?.chart2, chartColorPalettes.onshore),
      },
      {
        title: "Service - cost split by BP category",
        subtitle: "Actuals Rs Cr",
        total: Number(items?.chart3?.[0]?.ActualTotalValue || 0),
        centerLabel: "Rs Cr total",
        items: mapChartItems(items?.chart3, chartColorPalettes.service),
      },
    ],
    trendSeries: {
      labels: (items?.chart4 || []).map((entry) => entry.MonthYear),
      factory: (items?.chart4 || []).map((entry) =>
        Number(entry.FactoryActual || 0)
      ),
      onshore: (items?.chart4 || []).map((entry) =>
        Number(entry.OnshoreActual || 0)
      ),
      service: (items?.chart4 || []).map((entry) =>
        Number(entry.ServiceActual || 0)
      ),
    },
    excelFiles,
  };
};

const getDonutChartOption = (section) => ({
  color: section.items.map((item) => item.color),
  animation: false,
  tooltip: {
    trigger: "item",
    confine: true,
    formatter: ({ name, value, percent }) =>
      `${name}<br/>${Number(value).toFixed(2)} Cr (${percent}%)`,
    backgroundColor: "rgba(15, 23, 42, 0.92)",
    borderWidth: 0,
    textStyle: {
      color: "#f8fafc",
      fontSize: 12,
    },
  },
  series: [
    {
      type: "pie",
      radius: ["46%", "68%"],
      center: ["48%", "52%"],
      label: { show: false },
      labelLine: { show: false },
      avoidLabelOverlap: true,
      itemStyle: {
        borderRadius: 6,
        borderColor: "#fff",
        borderWidth: 1,
      },
      data: section.items.map((item) => ({
        value: Math.abs(Number(item.value || 0)),
        name: item.name,
        itemStyle: { color: item.color },
      })),
      emphasis: {
        itemStyle: {
          shadowBlur: 10,
          shadowOffsetX: 0,
          shadowColor: "rgba(0, 0, 0, 0.3)",
        },
      },
    },
  ],
  graphic: [
    {
      type: "text",
      left: "40%",
      top: "42%",
      bounding: "raw",
      style: {
        text: formatValue(section.total),
        fill: "#1e293b",
        fontSize: 18,
        fontWeight: 700,
        align: "center",
        verticalAlign: "middle",
      },
    },
    {
      type: "text",
      left: "40%",
      top: "56%",
      bounding: "raw",
      style: {
        text: section.centerLabel,
        fill: "#94a3b8",
        fontSize: 12,
        fontWeight: 700,
        align: "center",
        verticalAlign: "middle",
      },
    },
  ],
});

const getTrendChartOption = ({ labels, factory, onshore, service }) => ({
  animation: false,
  tooltip: {
    trigger: "axis",
    axisPointer: {
      type: "line",
      lineStyle: {
        color: "#cbd5e1",
        type: "dashed",
      },
    },
    backgroundColor: "rgba(15, 23, 42, 0.92)",
    borderWidth: 0,
    textStyle: {
      color: "#f8fafc",
      fontSize: 12,
    },
  },
  grid: {
    left: 42,
    right: 18,
    top: 24,
    bottom: 42,
  },
  xAxis: {
    type: "category",
    boundaryGap: false,
    data: labels,
    axisLine: {
      lineStyle: {
        color: "#dbe4f5",
      },
    },
    axisTick: { show: false },
    axisLabel: {
      fontSize: 12,
      fontWeight: "bold",
      color: "#000",
      margin: 14,
    },
    splitLine: {
      show: true,
      lineStyle: {
        color: "#eef2ff",
      },
    },
  },
  yAxis: {
    type: "value",
    axisLine: { show: false },
    axisTick: { show: false },
    axisLabel: {
      fontSize: 12,
      fontWeight: "bold",
      color: "#000",
    },
    splitLine: {
      lineStyle: {
        color: "#e6ecfb",
      },
    },
  },
  series: [
    {
      name: "Factory",
      type: "line",
      data: factory,
      z: 1,
      lineStyle: {
        color: "#4361ee",
        width: 2.5,
      },
      areaStyle: {
        color:  new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          { offset: 0, color: "rgba(67,97,238,0.35)" },
          { offset: 1, color: "rgba(67,97,238,0.08)" },
        ]),
      },
    },

    {
      name: "Onshore",
      type: "line",
      data: onshore,
      z: 3,
      lineStyle: {
        color: "#2a9d90",
        width: 2.5,
      },
      areaStyle: {
        color:  new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          { offset: 0, color: "rgba(42,157,144,0.35)" },
          { offset: 1, color: "rgba(42,157,144,0.08)" },
        ]),
      },
    },

    {
      name: "Service",
      type: "line",
      data: service,
      z: 2,
      lineStyle: {
        color: "#6d4cf5",
        width: 2.5,
      },
      areaStyle: {
        color:  new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          { offset: 0, color: "rgba(109,76,245,0.35)" },
          { offset: 1, color: "rgba(109,76,245,0.08)" },
        ]),
      },
    },
  ],
});

const EDADashboardDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedMonth, setSelectedMonth] = useState(DEFAULT_MONTH);
  const [selectedYear, setSelectedYear] = useState(DEFAULT_YEAR);
  const [dashboardData, setDashboardData] = useState({
    summaryCards: defaultSummaryCards,
    donutSections: defaultDonutSections,
    trendSeries: defaultTrendSeries,
    excelFiles: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const { dashboard, selectedProcess } = location.state || {};
  const dashboardName = dashboard?.eda_name;
  const title = formatDashboardTitle(dashboardName);
  const selectedMonthLabel =
    MONTHS.find((month) => month.value === selectedMonth)?.label || "March";

  const consolidatedReport = useMemo(
    () =>
      dashboardData.excelFiles.find((file) =>
        file.id.toLowerCase().includes("consolidated")
      ),
    [dashboardData.excelFiles]
  );

  const trendChartOption = useMemo(
    () => getTrendChartOption(dashboardData.trendSeries),
    [dashboardData.trendSeries]
  );

  useEffect(() => {
    if (!location.state) {
      return;
    }

    let ignore = false;

    const fetchDashboardData = async () => {
      setLoading(true);
      setError("");

      try {
        const response = await axiosInstance.post("/eda/eda1data", {
          Month: selectedMonth,
          Year: selectedYear,
        });

        if (!ignore && response?.data?.status && response?.data?.items) {
          setDashboardData(
            buildDashboardData(response.data.items, selectedMonth, selectedYear)
          );
        }
      } catch (apiError) {
        if (!ignore) {
          setError(
            apiError?.response?.data?.message || "Failed to fetch EDA data."
          );
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    fetchDashboardData();

    return () => {
      ignore = true;
    };
  }, [location.state, selectedMonth, selectedYear]);

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-gradient-to-b from-slate-50 to-white p-4 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">{title}</h1>
            <p className="mt-1 text-sm text-slate-500">
              Process: {formatDashboardTitle(selectedProcess)} | Status:{" "}
              {dashboard?.edaStatus || "Available"}
            </p>
          </div>

          <div className="flex items-center gap-2 self-start">
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800"
            >
              <FiArrowLeft className="h-4 w-4" />
              Back
            </button>
          </div>
        </div>

        {!location.state ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
            Open this page from the dashboard list so the selected dashboard
            context is available.
          </div>
        ) : (
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex flex-col gap-3 md:flex-row md:items-center">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-slate-400">Year</span>
                <select
                  value={selectedYear}
                  onChange={(event) =>
                    setSelectedYear(Number(event.target.value))
                  }
                  disabled={loading}
                  className="min-w-[120px] rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 outline-none transition focus:border-blue-300 disabled:cursor-not-allowed disabled:bg-slate-50"
                >
                  {YEAR.map((year) => (
                    <option key={year.value} value={year.value}>
                      {year.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-slate-400">
                  Month
                </span>
                <select
                  value={selectedMonth}
                  onChange={(event) =>
                    setSelectedMonth(Number(event.target.value))
                  }
                  disabled={loading}
                  className="min-w-[180px] rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 outline-none transition focus:border-blue-300 disabled:cursor-not-allowed disabled:bg-slate-50"
                >
                  {MONTHS.map((month) => (
                    <option key={month.value} value={month.value}>
                      {month.label}
                    </option>
                  ))}
                </select>
              </div>

              <button
                type="button"
                onClick={() => {
                  setSelectedMonth(DEFAULT_MONTH);
                  setSelectedYear(DEFAULT_YEAR);
                }}
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-500 shadow-sm transition hover:border-slate-300 hover:text-slate-700 disabled:cursor-not-allowed disabled:bg-slate-50"
              >
                <FiRefreshCcw className="h-3.5 w-3.5" />
                Reset to current month
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {loading && (
                <div className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-700">
                  <FiLoader className="h-4 w-4 animate-spin" />
                  Updating dashboard...
                </div>
              )}

              <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-medium text-emerald-700">
                <FiCalendar className="h-4 w-4" />
                Current period: {selectedMonthLabel} {selectedYear}
              </div>
            </div>
          </div>
        )}
      </div>

      {location.state && (
        <>
          {error && (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
            {dashboardData.summaryCards.map((card) => {
              const actual = card.metrics[0]?.value || 0;
              const baseline = card.metrics[1]?.value || 0;
              const target = card.metrics[2]?.value || 0;

              const variance =
                target !== 0
                  ? (((actual - target) / target) * 100).toFixed(0)
                  : 0;

              return (
                <div
                  key={card.title}
                  className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition hover:shadow-md"
                >
                  {/* Top Section */}
                  <div
                    className={`cursor-pointer p-6 text-white ${card.accent}`}
                    onClick={() =>
                      navigate("/Insights/eda-segment-detail", {
                        state: {
                          selectedProcess,
                          dashboard,
                          segment: card.title,
                          month: selectedMonth,
                          year: selectedYear,
                        },
                      })
                    }
                  >
                    {/* Header */}
                    <div className="mb-3 flex items-center gap-2">
                      <span className="text-sm opacity-80">{card.icon}</span>

                      <span className="text-lg font-semibold uppercase tracking-wide">
                        {card.title}
                      </span>
                    </div>

                    {/* Actual */}
                    <div className="mb-1">
                      <div className="flex items-baseline gap-2">
                        <div className="text-4xl font-bold leading-none">
                          {formatValue(actual)}
                        </div>
                        <div className="text-2xl opacity-90">Cr / month</div>
                      </div>
                    </div>
                  </div>

                  {/* Bottom Section */}
                  <div className="space-y-3 p-4">
                    <div className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3">
                      <div className="flex items-center gap-3">
                        <FiFlag className="h-5 w-5 text-slate-500" />
                        <span className="text-lg text-slate-700">Baseline per month</span>
                      </div>

                      <span className="text-xl font-medium text-slate-700">
                        {formatValue(baseline)}{" "}
                        <span className="text-base text-slate-500">Cr</span>
                      </span>
                    </div>

                    <div className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3">
                      <div className="flex items-center gap-3">
                        <FiTarget className="h-5 w-5 text-slate-500" />
                        <span className="text-lg text-slate-700">Target per month</span>
                      </div>

                      <span className="text-xl font-medium text-slate-700">
                        {formatValue(target)}{" "}
                        <span className="text-base text-slate-500">Cr</span>
                      </span>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-between border-t border-slate-200 px-5 py-4">
                    <p className="text-sm text-slate-400">
                      Click card to open BP summary
                    </p>

                    {card.xlsLink && (
                      <a
                        href={card.xlsLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 rounded-xl border border-emerald-400 px-4 py-2 text-sm font-medium text-emerald-700 transition hover:bg-emerald-50"
                      >
                        <FiDownload className="h-4 w-4" />
                        Download ↗
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="grid grid-cols-1 gap-4 2xl:grid-cols-3">
            {dashboardData.donutSections.map((section) => (
              <div
                key={section.title}
                className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
              >
                <div>
                  <h2 className="text-lg font-bold text-slate-800">
                    {section.title}
                  </h2>
                  <p className="text-sm font-semibold text-slate-400">
                    {section.subtitle}
                  </p>
                </div>

                <div className="mt-3">
                  <ReactECharts
                    option={getDonutChartOption(section)}
                    notMerge
                    lazyUpdate
                    showLoading={loading}
                    style={{ height: 230, width: "100%" }}
                  />
                </div>

                <div className="mt-3 space-y-2">
                  {section.items.slice(0, 6).map((item) => (
                    <div
                      key={item.name}
                      className="flex items-center justify-between gap-3 border-b border-slate-100 pb-1.5 text-sm last:border-b-0"
                    >
                      <div className="flex min-w-0 items-center gap-2">
                        <span
                          className="h-2.5 w-2.5 rounded-[3px]"
                          style={{ backgroundColor: item.color }}
                        />
                        <span className="truncate text-slate-600 font-semibold">
                          {item.name}
                        </span>
                      </div>
                      <span className="font-mono text-slate-800 font-semibold">
                        {formatValue(item.value)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <h2 className="text-lg font-bold text-slate-800">
                  All three segments - monthly cumulative trend (Rs Cr)
                </h2>
                <p className="text-sm text-slate-400 font-semibold">
                  Factory total - Onshore total - Service total across available
                  months
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-4 text-sm font-bold">
                {[
                  { label: "Factory", color: "#4361ee" },
                  { label: "Onshore", color: "#2a9d90" },
                  { label: "Service", color: "#6d4cf5" },
                ].map((legend) => (
                  <div
                    key={legend.label}
                    className="inline-flex items-center gap-2 text-slate-500"
                  >
                    <span
                      className="h-2.5 w-2.5 rounded-sm"
                      style={{ backgroundColor: legend.color }}
                    />
                    {legend.label}
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-4 overflow-x-auto">
              <div className="min-w-[900px]">
                <ReactECharts
                  option={trendChartOption}
                  notMerge
                  lazyUpdate
                  showLoading={loading}
                  style={{ height: 320, width: "100%" }}
                />
              </div>
            </div>
          </div>

          {consolidatedReport && (
            <div className="flex justify-end">
              <a
                href={consolidatedReport.xlsLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700 shadow-sm transition hover:bg-emerald-100"
              >
                <FiDownload className="h-4 w-4" />
                Download Final Consolidated Report
              </a>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EDADashboardDetail;
